// FilterBar (DESIGN §3.5.4): eine Zeile über der Tabelle in der Karte – runder Suchknopf 44 × 40
// `field` (klappt zu einem Suchfeld 240 auf; mit Wert bleibt es offen), Dropdown-Pills,
// SegmentedControl; rechts Aktionen; Abstand 12. Aktive Filter als Chips mit × darunter, „Clear
// filters“. Mobil: Suche + Pill „Filters · n“ (Sheet von unten) in Zeile 1, Aktionen in Zeile 2.
// Den Zustand in der URL (`?q=&level=…`) hält der Aufrufer.
import { useRef, useState, type ReactNode } from 'react';
import { FunnelSimpleIcon, MagnifyingGlassIcon } from '@phosphor-icons/react';
import { Button } from './Button';
import { FilterChip } from './FilterChip';
import { IconButton } from './IconButton';
import { Input } from './Input';
import { Sheet } from './Sheet';
import { cn } from '../basis/kern';

export interface FilterSearchProps {
  value: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  /** Name des Suchfelds bzw. -knopfs. */
  label?: string;
  /** Immer offen (mobil, Seiten mit Suche als Hauptsache). */
  expanded?: boolean;
  className?: string;
}

export function FilterSearch({
  value,
  onValueChange,
  placeholder = 'Search',
  label = 'Search',
  expanded,
  className,
}: FilterSearchProps) {
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const isOpen = expanded || open || value !== '';
  if (!isOpen) {
    return (
      <IconButton
        label={label}
        icon={<MagnifyingGlassIcon />}
        variant="secondary"
        size="lg"
        tooltip={false}
        className={cn('bg-field! hover:bg-field-hover!', className)}
        onClick={() => {
          setOpen(true);
          requestAnimationFrame(() => inputRef.current?.focus());
        }}
      />
    );
  }
  return (
    <Input
      ref={inputRef}
      type="search"
      value={value}
      aria-label={label}
      placeholder={placeholder}
      onChange={(event) => onValueChange(event.target.value)}
      onBlur={() => setOpen(false)}
      frameClassName={cn(
        'rounded-full bg-field',
        expanded ? 'w-full' : 'w-60 max-md:w-full',
        className,
      )}
    />
  );
}

export interface ActiveFilter {
  key: string;
  label: string;
  avatar?: ReactNode;
  onRemove: () => void;
}

export interface FilterBarProps {
  search?: FilterSearchProps;
  /** Dropdown-Pills (`Select variant="pill"`), FilterChips. */
  filters?: ReactNode;
  /** SegmentedControl (z. B. Ebene Company · Department · Team · Personal). */
  segment?: ReactNode;
  /** Rechts: Primär-Pill, Sekundär-Pills, „…“. */
  actions?: ReactNode;
  activeFilters?: ActiveFilter[];
  onClearFilters?: () => void;
  /** Zahl im mobilen Knopf „Filters · n“ (Standard: Anzahl aktiver Filter). */
  filterCount?: number;
  /** Fläche, auf der die Leiste liegt (Chips). */
  surface?: 'page' | 'card';
  className?: string;
}

export function FilterBar({
  search,
  filters,
  segment,
  actions,
  activeFilters = [],
  onClearFilters,
  filterCount,
  surface = 'card',
  className,
}: FilterBarProps) {
  const [sheetOpen, setSheetOpen] = useState(false);
  const count = filterCount ?? activeFilters.length;
  const hasFilterControls = Boolean(filters || segment);
  return (
    <div role="group" aria-label="Filters" className={cn('flex flex-col gap-3', className)}>
      {/* Desktop: eine Zeile. */}
      <div className="hidden flex-wrap items-center gap-3 md:flex">
        {search ? <FilterSearch {...search} /> : null}
        {filters}
        {segment}
        {actions ? (
          <div className="ml-auto flex flex-wrap items-center gap-3">{actions}</div>
        ) : null}
      </div>
      {/* Mobil: Suche + Filter-Sheet, Aktionen darunter. */}
      <div className="flex flex-col gap-3 md:hidden">
        <div className="flex items-center gap-3">
          {search ? (
            <div className="min-w-0 flex-1">
              <FilterSearch {...search} expanded />
            </div>
          ) : null}
          {hasFilterControls ? (
            <Button
              variant="secondary"
              surface={surface}
              icon={<FunnelSimpleIcon size={16} />}
              className="bg-field hover:bg-field-hover"
              onClick={() => setSheetOpen(true)}
            >
              {count > 0 ? `Filters · ${count}` : 'Filters'}
            </Button>
          ) : null}
        </div>
        {actions ? <div className="flex flex-wrap items-center gap-3">{actions}</div> : null}
      </div>
      {activeFilters.length ? (
        <div className="flex flex-wrap items-center gap-2">
          {activeFilters.map((filter) => (
            <FilterChip
              key={filter.key}
              avatar={filter.avatar}
              onRemove={filter.onRemove}
              surface={surface}
            >
              {filter.label}
            </FilterChip>
          ))}
          {onClearFilters ? (
            <Button variant="link" size="sm" onClick={onClearFilters} className="ml-1">
              Clear filters
            </Button>
          ) : null}
        </div>
      ) : null}
      {hasFilterControls ? (
        <Sheet
          open={sheetOpen}
          onOpenChange={setSheetOpen}
          side="bottom"
          title="Filters"
          footer={
            <>
              {onClearFilters ? (
                <Button variant="secondary" surface="card" onClick={onClearFilters}>
                  Clear filters
                </Button>
              ) : null}
              <Button variant="primary" onClick={() => setSheetOpen(false)}>
                Show results
              </Button>
            </>
          }
        >
          <div className="flex flex-col items-start gap-4">
            {filters}
            {segment}
          </div>
        </Sheet>
      ) : null}
    </div>
  );
}
